<template>
  <div class="container-xl px-4 movements-page">
    <div class="row">
      <div class="col-12">
        <div class="page-header-mobile d-flex justify-content-between align-items-center mb-3 mt-2">
          <h2 class="fw-bold mb-0 page-title-mobile">Movimientos de Inventario</h2>
          <button class="btn btn-dark d-flex align-items-center btn-mobile-primary" @click="openCreateModal">
            <i class="bi bi-plus-lg me-2"></i> Nuevo Movimiento
          </button>
        </div>
      </div>
    </div>

    <div class="mobile-context-chip d-lg-none">
      <span class="mobile-context-label">Catálogo e Inventario</span>
      <strong>Movimientos</strong>
    </div>

    <div class="alert alert-light border d-flex flex-wrap justify-content-between align-items-center gap-2 operational-summary-card">
      <div>
        <strong>Sucursal {{ currentSucursalId }}</strong>
        <span class="text-muted ms-2">Modo: {{ modeLabel(operationalConfig.modo_operacion_sucursal) }}</span>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <span class="badge bg-dark">Mostrador: {{ counterWarehouseName }}</span>
        <span v-if="operationalConfig.auto_transferir_a_mostrador" class="badge bg-secondary">Autoabastecimiento activo</span>
      </div>
    </div>

    <div class="mb-3 d-flex flex-wrap align-items-end gap-2 mobile-filter-card">
      <div class="btn-group me-2" role="group" aria-label="Filtro por tipo">
        <button class="btn btn-sm" :class="!filters.tipo ? 'btn-dark' : 'btn-outline-secondary'" @click="setTipo(null)">Todos</button>
        <button class="btn btn-sm" :class="filters.tipo==='ENTRADA' ? 'btn-dark' : 'btn-outline-secondary'" @click="setTipo('ENTRADA')">Entradas</button>
        <button class="btn btn-sm" :class="filters.tipo==='SALIDA' ? 'btn-dark' : 'btn-outline-secondary'" @click="setTipo('SALIDA')">Salidas</button>
        <button class="btn btn-sm" :class="filters.tipo==='AJUSTE' ? 'btn-dark' : 'btn-outline-secondary'" @click="setTipo('AJUSTE')">Ajustes</button>
      </div>

      <div class="row g-2 w-100">
        <div class="col-6 col-md-3 col-lg-2">
          <label class="form-label mb-1">Desde</label>
          <input
            type="text"
            class="form-control form-control-sm"
            ref="desdeInput"
            :value="filters.desde || ''"
            placeholder="dd/mm/yyyy"
            readonly
          />
        </div>
        <div class="col-6 col-md-3 col-lg-2">
          <label class="form-label mb-1">Hasta</label>
          <input
            type="text"
            class="form-control form-control-sm"
            ref="hastaInput"
            :value="filters.hasta || ''"
            placeholder="dd/mm/yyyy"
            readonly
          />
        </div>
        <div class="col-12 col-md-6 col-lg-4 d-flex align-items-end">
          <div class="d-flex gap-2 w-100">
            <button class="btn btn-sm btn-outline-secondary flex-fill" @click="applyFilters">Buscar</button>
            <button class="btn btn-sm btn-outline-danger" @click="clearFilters">Limpiar</button>
          </div>
        </div>
      </div>
    </div>

    <div class="mobile-results-summary d-lg-none">
      <div class="mobile-results-pill">
        <span class="mobile-results-value">{{ movements.length }}</span>
        <span class="mobile-results-copy">movimientos visibles</span>
      </div>
      <div class="mobile-results-pill secondary">
        <span class="mobile-results-copy">Total</span>
        <span class="mobile-results-value small">{{ total }}</span>
      </div>
      <div v-if="filters.tipo" class="mobile-results-pill secondary">
        <span class="mobile-results-copy">Filtro</span>
        <span class="mobile-results-value small">{{ filters.tipo }}</span>
      </div>
    </div>

    <div class="bg-white rounded shadow-sm p-4 d-none d-lg-block">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <small class="text-muted">Total: {{ total }}</small>
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="form-label mb-0 me-2"><small>Filas</small></label>
          <select class="form-select form-select-sm" style="width:auto" v-model.number="itemsPerPage" @change="changePageSize">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
          </select>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Producto</th>
              <th>Lote</th>
              <th>Cantidad</th>
              <th>Depósito</th>
              <th>Usuario</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-4">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cargando...
              </td>
            </tr>
            <tr v-else-if="!movements.length">
              <td colspan="8" class="text-center py-4 text-muted">Sin resultados</td>
            </tr>
            <tr v-else v-for="mov in movements" :key="mov.movimiento_id">
              <td>{{ formatDate(mov.creado_en) }}</td>
              <td>
                <span :class="[
                    'badge rounded-pill px-3 py-2',
                    mov.tipo_movimiento === 'ENTRADA'
                      ? 'bg-success text-white fw-bold'
                      : mov.tipo_movimiento === 'SALIDA'
                        ? 'bg-danger text-white fw-bold'
                        : 'bg-light text-dark fw-bold'
                  ]">
                  {{ mov.tipo_movimiento }}
                </span>
              </td>
              <td>{{ mov.producto_nombre }}</td>
              <td>{{ mov.lote_id }}</td>
              <td :class="qtyClass(mov)">
                {{ qtyText(mov) }}
              </td>
              <td>{{ mov.deposito_nombre }}</td>
              <td>{{ mov.creado_por_nombre }}</td>
              <td>
                <div>{{ mov.motivo }}</div>
                <small v-if="isAutoTransfer(mov)" class="text-primary fw-semibold">Movimiento automático a mostrador</small>
                <div v-if="hasPresentationTrace(mov)" class="presentation-trace mt-2">
                  <div class="presentation-trace-title">Presentación original</div>
                  <div v-for="trace in presentationTraceItems(mov)" :key="trace.key" class="presentation-trace-line">
                    <span class="badge bg-info-subtle text-info-emphasis">{{ trace.type }}</span>
                    <span>{{ trace.label }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination
        class="mt-3"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="changePage"
      />
    </div>

    <div class="mobile-movement-list d-lg-none">
      <div v-if="loading" class="mobile-empty-state">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>Cargando movimientos...</span>
      </div>
      <div v-else-if="!movements.length" class="mobile-empty-state">
        <i class="bi bi-arrow-left-right"></i>
        <span>Sin resultados</span>
      </div>
      <div v-else v-for="mov in movements" :key="`mobile-${mov.movimiento_id}`" class="mobile-movement-card">
        <div class="mobile-movement-top">
          <div>
            <p class="mobile-movement-date">{{ formatDate(mov.creado_en) }}</p>
            <h3 class="mobile-movement-product">{{ mov.producto_nombre }}</h3>
          </div>
          <span :class="[
            'badge rounded-pill px-3 py-2',
            mov.tipo_movimiento === 'ENTRADA'
              ? 'bg-success text-white fw-bold'
              : mov.tipo_movimiento === 'SALIDA'
                ? 'bg-danger text-white fw-bold'
                : 'bg-light text-dark fw-bold border'
          ]">
            {{ mov.tipo_movimiento }}
          </span>
        </div>

        <div class="mobile-movement-meta">
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Cantidad</span>
            <strong :class="qtyClass(mov)">{{ qtyText(mov) }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Depósito</span>
            <strong>{{ mov.deposito_nombre || 'Sin depósito' }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Usuario</span>
            <strong>{{ mov.creado_por_nombre || 'Sin usuario' }}</strong>
          </div>
          <div class="mobile-meta-item">
            <span class="mobile-meta-label">Lote</span>
            <strong>{{ mov.lote_id || '—' }}</strong>
          </div>
        </div>

        <div class="mobile-movement-note">
          <div>{{ mov.motivo || 'Sin motivo' }}</div>
          <small v-if="isAutoTransfer(mov)" class="text-primary fw-semibold">Movimiento automático a mostrador</small>
          <div v-if="hasPresentationTrace(mov)" class="presentation-trace mt-2">
            <div class="presentation-trace-title">Presentación original</div>
            <div v-for="trace in presentationTraceItems(mov)" :key="trace.key" class="presentation-trace-line">
              <span class="badge bg-info-subtle text-info-emphasis">{{ trace.type }}</span>
              <span>{{ trace.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <AppPagination
        class="mt-3"
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-changed="changePage"
      />
    </div>

    <!-- Modal: Nuevo Movimiento (custom) -->
    <div v-if="showCreateModal">
      <div class="mv-backdrop" @click="closeCreateModal"></div>
      <div class="mv-modal" role="dialog" @click.stop>
        <div class="mv-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Mover producto entre depósitos</h5>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeCreateModal" aria-label="Close">✕</button>
        </div>
        <div class="mv-body">
          <form @submit.prevent>
            <div class="row g-2">
              <div class="col-12 col-md-8">
                <label class="form-label">Código de barras / código de producto</label>
                <div class="input-group">
                  <input
                    ref="productCodeInput"
                    type="text"
                    class="form-control"
                    v-model.trim="newMovement.codigo_producto_input"
                    placeholder="Escanear o escribir código"
                    autocomplete="off"
                    @keydown.enter.prevent="handleResolveProduct"
                    required
                  />
                  <button type="button" class="btn btn-outline-secondary" @click="openProductSearchModal">
                    Buscar
                  </button>
                </div>
                <small v-if="selectedProduct" class="text-muted">
                  {{ selectedProduct.codigo_producto || selectedProduct.codigo_barras || `ID ${selectedProduct.producto_id}` }} - {{ selectedProduct.nombre }}
                </small>
              </div>
              <div class="col-12 col-md-4">
                <label class="form-label">Solicita / aprueba</label>
                <input type="text" class="form-control" :value="currentUserName" readonly />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Ejecutor</label>
                <select class="form-select" v-model.number="newMovement.usuario_ejecuta_id" required>
                  <option :value="null" disabled>Seleccionar ejecutor</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">
                    {{ user.nombre }}
                  </option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Depósito Origen</label>
                <select class="form-select" v-model.number="newMovement.deposito_origen_id" required>
                  <option :value="null" disabled>Seleccionar origen</option>
                  <option v-for="dep in operationalWarehouses" :key="'origen_'+dep.id" :value="dep.id">
                    {{ dep.nombre }}
                  </option>
                </select>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Lote disponible</label>
                <select class="form-select" v-model="newMovement.lote_selection" :disabled="loadingLots || !selectedProduct" required>
                  <option value="" disabled>
                    {{ loadingLots ? 'Cargando lotes...' : 'Seleccionar lote disponible' }}
                  </option>
                  <option v-for="lot in availableLots" :key="`${lot.lote_id}_${lot.deposito_id}`" :value="formatLotKey(lot)">
                    {{ formatLotOption(lot) }}
                  </option>
                </select>
                <small class="text-muted">
                  {{ lotHint }}
                </small>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Depósito Destino</label>
                <select class="form-select" v-model.number="newMovement.deposito_destino_id" required>
                  <option :value="null" disabled>Seleccionar destino</option>
                  <option v-for="dep in operationalWarehouses" :key="'destino_'+dep.id" :value="dep.id">
                    {{ dep.nombre }}
                  </option>
                </select>
                <small class="text-muted">
                  {{ destinationHint }}
                </small>
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Cantidad</label>
                <input type="number" min="1" class="form-control" v-model.number="newMovement.cantidad" required />
              </div>
              <div class="col-12 col-md-6">
                <label class="form-label">Motivo</label>
                <input type="text" class="form-control" v-model="newMovement.motivo" placeholder="Reabastecer Mostrador" />
              </div>
            </div>
          </form>
          <div v-if="createError" class="alert alert-danger mt-3">
            {{ createError }}
          </div>
        </div>
        <div class="mv-footer d-flex justify-content-end gap-2">
          <button class="btn btn-outline-secondary" :disabled="creating" @click="closeCreateModal">Cancelar</button>
          <button class="btn btn-dark" :disabled="creating" @click="submitCreate">
            <span v-if="creating" class="spinner-border spinner-border-sm me-2"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>

    <div v-if="showProductSearchModal">
      <div class="mv-backdrop mv-backdrop-top" @click="closeProductSearchModal"></div>
      <div class="mv-modal mv-modal-search" role="dialog" @click.stop>
        <div class="mv-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Buscar producto con stock disponible</h5>
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeProductSearchModal" aria-label="Close">✕</button>
        </div>
        <div class="mv-body">
          <div class="mb-3">
            <label class="form-label">Buscar por código o nombre</label>
            <input
              type="text"
              class="form-control"
              v-model.trim="productSearchQuery"
              placeholder="Código de barras, código de producto o nombre"
              autocomplete="off"
            />
          </div>
          <div v-if="searchingProducts" class="text-muted small mb-2">
            Buscando productos...
          </div>
          <div class="list-group">
            <button
              v-for="product in productSuggestions"
              :key="product.producto_id"
              type="button"
              class="list-group-item list-group-item-action"
              @click="selectProductSuggestion(product)"
            >
              <div class="fw-semibold">{{ product.nombre }}</div>
              <small class="text-muted">
                {{ product.codigo_producto || product.codigo_barras || `ID ${product.producto_id}` }} · Stock {{ product.stock_disponible }}
              </small>
            </button>
            <div v-if="!searchingProducts && productSearchQuery.length >= 2 && !productSuggestions.length" class="text-muted small">
              No hay productos con stock disponible para esa búsqueda.
            </div>
            <div v-if="productSearchQuery.length < 2" class="text-muted small">
              Escribe al menos 2 caracteres para buscar.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import api from '@/services/apiService'
import operationalConfigService from '@/services/operationalConfigService'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'
import { Spanish } from 'flatpickr/dist/l10n/es.js'

// Estado de lista y filtros
const movements = ref([])
const total = ref(0)
const loading = ref(false)
const currentSucursalId = operationalConfigService.getCurrentSucursalId()
const operationalConfig = ref(operationalConfigService.getConfig(currentSucursalId))
const warehouses = ref([])
const operationalWarehouses = computed(() =>
  warehouses.value.filter(dep => dep.activo && dep.habilitado)
)
const users = ref([])
const availableLots = ref([])
const selectedProduct = ref(null)
const productSuggestions = ref([])
const loadingProduct = ref(false)
const loadingLots = ref(false)
const searchingProducts = ref(false)
const showProductSearchModal = ref(false)
const productSearchQuery = ref('')
const currentUserId = getCurrentUserId()
const counterWarehouseName = computed(() =>
  warehouses.value.find(dep => dep.id === operationalConfig.value.deposito_mostrador_id)?.nombre || 'Sin definir'
)
const currentUserName = computed(() =>
  users.value.find(user => Number(user.id) === Number(currentUserId))?.nombre || `Usuario actual #${currentUserId || '-'}`
)
const destinationHint = computed(() => {
  const resolved = operationalConfigService.resolveDestinationWarehouse({
    purpose: 'recepcion',
    sucursalId: currentSucursalId,
    warehouses: warehouses.value
  })
  if (resolved.reason === 'mostrador_unico') return 'Destino sugerido automáticamente: mostrador único.'
  if (resolved.reason === 'default_recepcion') return 'Destino sugerido por configuración default de recepción.'
  return 'Seleccione manualmente el depósito destino.'
})
const lotHint = computed(() => {
  if (!selectedProduct.value) return 'Primero resuelva el producto por código.'
  if (loadingLots.value) return 'Consultando lotes con stock disponible.'
  if (!availableLots.value.length) return 'No hay lotes con stock disponible para el criterio seleccionado.'
  if (!newMovement.deposito_origen_id) return 'Se muestran lotes disponibles de la sucursal; puede filtrar por depósito origen.'
  return 'Sólo se muestran lotes con stock disponible en el depósito origen seleccionado.'
})

const currentPage = ref(1)
const itemsPerPage = ref(20)
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / itemsPerPage.value)))

const filters = reactive({
  tipo: null, // ENTRADA | SALIDA | AJUSTE | null
  desde: null,
  hasta: null,
})

const productCodeInput = ref(null)
const desdeInput = ref(null)
const hastaInput = ref(null)
let desdePicker = null
let hastaPicker = null
let syncingOriginFromLotSelection = false
let productSearchDebounce = null

const DATE_FORMAT = 'd/m/Y'

function ddmmyyyyToDate(val) {
  const iso = convertDateToISO(val)
  return iso ? new Date(`${iso}T00:00:00`) : null
}

function updatePickerDate(picker, value) {
  if (!picker) return
  if (!value) {
    picker.clear()
  } else {
    const dateObj = ddmmyyyyToDate(value)
    if (dateObj) {
      picker.setDate(dateObj, false, DATE_FORMAT)
    } else {
      picker.clear()
    }
  }
}

function setupDatePickers() {
  if (desdeInput.value) {
    desdePicker = flatpickr(desdeInput.value, {
      dateFormat: DATE_FORMAT,
      locale: Spanish,
      defaultDate: ddmmyyyyToDate(filters.desde),
      allowInput: false,
      onChange(_, dateStr) {
        filters.desde = dateStr || null
      }
    })
  }

  if (hastaInput.value) {
    hastaPicker = flatpickr(hastaInput.value, {
      dateFormat: DATE_FORMAT,
      locale: Spanish,
      defaultDate: ddmmyyyyToDate(filters.hasta),
      allowInput: false,
      onChange(_, dateStr) {
        filters.hasta = dateStr || null
      }
    })
  }
}

function setTipo(value) {
  filters.tipo = value
  // Buscar inmediatamente al cambiar el tipo
  applyFilters()
}

const dtfPy = new Intl.DateTimeFormat('es-PY', {
  timeZone: 'America/Asuncion',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function formatDate(iso) {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    return dtfPy.format(d)
  } catch {
    return iso
  }
}

function formatDateShort(iso) {
  if (!iso) return 'sin vencimiento'
  try {
    return new Intl.DateTimeFormat('es-PY', {
      timeZone: 'America/Asuncion',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function convertDateToISO(ddmmyyyy) {
  if (!ddmmyyyy || !ddmmyyyy.match(/^\d{2}\/\d{2}\/\d{4}$/)) return null
  const [day, month, year] = ddmmyyyy.split('/')
  return `${year}-${month}-${day}`
}

function qtyClass(mov) {
  const tipo = mov?.tipo_movimiento
  if (tipo === 'ENTRADA') return 'text-success'
  if (tipo === 'SALIDA') return 'text-danger'
  return 'text-secondary'
}

function qtyText(mov) {
  const tipo = mov?.tipo_movimiento
  const val = Math.abs(Number(mov?.cantidad || 0))
  if (tipo === 'ENTRADA') return `+${val}`
  if (tipo === 'SALIDA') return `-${val}`
  // AJUSTE u otros: sin signo
  return String(val)
}

function formatQuantity(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  return new Intl.NumberFormat('es-PY', { maximumFractionDigits: 3 }).format(number)
}

function presentationComponents(mov) {
  return Array.isArray(mov?.presentacion_componentes) ? mov.presentacion_componentes : []
}

function hasPresentationTrace(mov) {
  return presentationComponents(mov).length > 0
}

function presentationTraceItems(mov) {
  return presentationComponents(mov).map((component, index) => {
    const presentationQty = formatQuantity(component.cantidad_presentacion_original)
    const presentationName = component.presentacion_nombre || 'presentación'
    const convertedQty = formatQuantity(component.cantidad_operativa_convertida)
    const unit = component.unidad_medida_nombre || 'unidad operativa'
    const factor = component.factor_aplicado != null
      ? ` · factor ${formatQuantity(component.factor_aplicado)}`
      : ''
    return {
      key: `${component.movimiento_componente_id || component.presentacion_id || 'trace'}-${index}`,
      type: component.tipo_presentacion || 'PRESENTACION',
      label: `${presentationQty} ${presentationName} -> ${convertedQty} ${unit}${factor}`
    }
  })
}

function modeLabel(mode) {
  if (mode === 'MONODEPOSITO_AUTO_MOSTRADOR') return 'Monodepósito auto mostrador'
  if (mode === 'MULTIDEPOSITO_DEPOSITO_DEFAULT') return 'Multidepósito con default'
  return 'Multidepósito manual'
}

function isAutoTransfer(mov) {
  return operationalConfigService.isAutoTransferMovement(mov)
}

function normalizeWarehouse(raw) {
  return {
    id: raw.deposito_id,
    sucursalId: raw.sucursal_id,
    nombre: raw.nombre,
    activo: !!raw.activo,
    habilitado: !!raw.habilitado
  }
}

function normalizeUser(raw) {
  return {
    id: raw.usuario_id,
    nombre: raw.nombre || raw.usuario || `Usuario #${raw.usuario_id}`
  }
}

function getCurrentUserId() {
  const token = localStorage.getItem('authToken')
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return Number(payload?.user_id) || null
  } catch {
    return null
  }
}

function formatLotOption(lot) {
  const depLabel = lot.deposito_nombre ? ` - ${lot.deposito_nombre}` : ''
  return `Lote ${lot.lote_id}${depLabel} - Stock ${lot.cantidad_disponible} - Vence ${formatDateShort(lot.fecha_vencimiento)}`
}

function formatLotKey(lot) {
  return `${lot.lote_id}:${lot.deposito_id}`
}

function applyLotSelection(selection) {
  if (!selection) {
    newMovement.lote_id = null
    return
  }
  const [lotId, depositoId] = String(selection).split(':')
  newMovement.lote_id = Number(lotId) || null
  if (!newMovement.deposito_origen_id && depositoId) {
    syncingOriginFromLotSelection = true
    newMovement.deposito_origen_id = Number(depositoId) || null
  }
}

async function loadWarehouses() {
  try {
    const { data } = await api.get('/api/inventory/warehouses')
    const rows = Array.isArray(data?.depositos) ? data.depositos : []
    warehouses.value = rows
      .map(normalizeWarehouse)
      .filter(dep => Number(dep.sucursalId) === Number(currentSucursalId))
    operationalConfig.value = operationalConfigService.getConfig(currentSucursalId)
  } catch (_) {
    warehouses.value = []
  }
}

async function loadUsers() {
  try {
    const { data } = await api.get('/api/auth/users', { limit: 100 })
    users.value = Array.isArray(data?.data) ? data.data.map(normalizeUser) : []
  } catch (_) {
    users.value = []
  }
}

async function fetchAvailableLots() {
  availableLots.value = []
  newMovement.lote_id = null
  newMovement.lote_selection = ''

  if (!selectedProduct.value?.producto_id) return

  loadingLots.value = true
  try {
    const { data } = await api.get(`/api/inventory/product/${selectedProduct.value.producto_id}/available-lots`, {
      sucursal_id: currentSucursalId,
      deposito_id: newMovement.deposito_origen_id || undefined
    })
    availableLots.value = Array.isArray(data?.items) ? data.items : []
  } catch (_) {
    availableLots.value = []
  } finally {
    loadingLots.value = false
  }
}

async function resolveProductForMovement() {
  const code = String(newMovement.codigo_producto_input || '').trim()
  if (!code) {
    selectedProduct.value = null
    availableLots.value = []
    newMovement.producto_id = null
    newMovement.lote_id = null
    newMovement.lote_selection = ''
    return
  }

  loadingProduct.value = true
  try {
    const { data } = await api.get(`/api/inventory/product/code/${encodeURIComponent(code)}`)
    selectedProduct.value = data || null
    newMovement.producto_id = selectedProduct.value?.producto_id || null
    productSuggestions.value = []
    await fetchAvailableLots()
  } catch (error) {
    selectedProduct.value = null
    availableLots.value = []
    newMovement.producto_id = null
    newMovement.lote_id = null
    newMovement.lote_selection = ''
    createError.value = error?.response?.data?.message || 'No se pudo resolver el producto'
  } finally {
    loadingProduct.value = false
  }
}

async function handleResolveProduct() {
  createError.value = ''
  await resolveProductForMovement()
}

function openProductSearchModal() {
  productSearchQuery.value = String(newMovement.codigo_producto_input || '').trim()
  showProductSearchModal.value = true
  productSuggestions.value = []
  clearTimeout(productSearchDebounce)
  if (productSearchQuery.value.length >= 2) {
    searchProductsForMovement(productSearchQuery.value)
  }
}

function closeProductSearchModal() {
  showProductSearchModal.value = false
}

async function searchProductsForMovement(query) {
  const normalizedQuery = String(query || '').trim()
  if (normalizedQuery.length < 2) {
    productSuggestions.value = []
    return
  }

  searchingProducts.value = true
  try {
    const { data } = await api.get('/api/inventory/products/search', {
      q: normalizedQuery,
      sucursal_id: currentSucursalId,
      limit: 8
    })
    productSuggestions.value = Array.isArray(data?.items) ? data.items : []
  } catch (_) {
    productSuggestions.value = []
  } finally {
    searchingProducts.value = false
  }
}

async function selectProductSuggestion(product) {
  selectedProduct.value = product
  newMovement.codigo_producto_input = product.codigo_producto || product.codigo_barras || ''
  newMovement.producto_id = product.producto_id
  productSuggestions.value = []
  closeProductSearchModal()
  createError.value = ''
  await fetchAvailableLots()
}

async function fetchMovements() {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      pageSize: itemsPerPage.value,
    }
    if (filters.tipo) params.tipo = filters.tipo
    // Nota: el backend requiere 'desde' y 'hasta' en minúsculas
    const desdeISO = convertDateToISO(filters.desde)
    const hastaISO = convertDateToISO(filters.hasta)
    if (desdeISO) params.desde = desdeISO
    if (hastaISO) params.hasta = hastaISO

    const { data } = await api.get('/api/inventory/warehouse/movements', params)
    movements.value = Array.isArray(data?.items) ? data.items : []
    total.value = Number.isFinite(data?.total) ? data.total : movements.value.length
    // Opcional: alinear currentPage si backend lo devuelve
    if (data?.page) currentPage.value = data.page
  } catch (e) {
    // apiService ya muestra alert con mensaje del backend
    movements.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

function changePage(page) {
  if (page < 1 || page > totalPages.value) return
  currentPage.value = page
  fetchMovements()
}

function changePageSize() {
  currentPage.value = 1
  fetchMovements()
}

function applyFilters() {
  currentPage.value = 1
  fetchMovements()
}

function clearFilters() {
  // Limpiar fechas y tipo
  filters.desde = null
  filters.hasta = null
  filters.tipo = null
  currentPage.value = 1
  updatePickerDate(desdePicker, null)
  updatePickerDate(hastaPicker, null)
  fetchMovements()
}

onMounted(() => {
  setupDatePickers()
  loadWarehouses()
  loadUsers()
  fetchMovements()
})

onBeforeUnmount(() => {
  clearTimeout(productSearchDebounce)
  if (desdePicker) {
    desdePicker.destroy()
    desdePicker = null
  }
  if (hastaPicker) {
    hastaPicker.destroy()
    hastaPicker = null
  }
})

// ─── Crear nuevo movimiento (mover producto entre depósitos) ───
const showCreateModal = ref(false)
const creating = ref(false)
const createError = ref('')
const newMovement = reactive({
  codigo_producto_input: '',
  producto_id: null,
  lote_selection: '',
  lote_id: null,
  cantidad: 1,
  deposito_origen_id: null,
  deposito_destino_id: null,
  usuario_ejecuta_id: null,
  motivo: ''
})

function resetCreateForm() {
  newMovement.codigo_producto_input = ''
  newMovement.producto_id = null
  newMovement.lote_selection = ''
  newMovement.lote_id = null
  newMovement.cantidad = 1
  newMovement.deposito_origen_id = null
  newMovement.deposito_destino_id = null
  newMovement.usuario_ejecuta_id = null
  newMovement.motivo = ''
  createError.value = ''
  selectedProduct.value = null
  availableLots.value = []
  productSuggestions.value = []
  productSearchQuery.value = ''
  closeProductSearchModal()

  const resolved = operationalConfigService.resolveDestinationWarehouse({
    purpose: 'recepcion',
    sucursalId: currentSucursalId,
    warehouses: warehouses.value
  })
  if (!resolved.requiresSelection && resolved.depositoId) {
    newMovement.deposito_destino_id = resolved.depositoId
  }
}

function openCreateModal() {
  resetCreateForm()
  showCreateModal.value = true
  setTimeout(() => {
    productCodeInput.value?.focus()
    productCodeInput.value?.select?.()
  }, 0)
}

function closeCreateModal() {
  showCreateModal.value = false
}

function validateCreate() {
  if (!newMovement.codigo_producto_input) return 'Código de producto o código de barras es requerido'
  if (!newMovement.producto_id) return 'Debe resolver un producto válido antes de guardar'
  if (!newMovement.usuario_ejecuta_id) return 'Ejecutor es requerido'
  if (!newMovement.deposito_origen_id) return 'Depósito origen es requerido'
  if (!newMovement.lote_id) return 'Debe seleccionar un lote con stock disponible'
  if (!newMovement.deposito_destino_id) return 'Depósito destino es requerido'
  if (newMovement.deposito_origen_id === newMovement.deposito_destino_id) return 'Origen y destino deben ser diferentes'
  if (!newMovement.cantidad || newMovement.cantidad <= 0) return 'Cantidad debe ser mayor a 0'
  return ''
}

async function submitCreate() {
  createError.value = validateCreate()
  if (createError.value) return
  creating.value = true
  try {
    const payload = {
      producto_id: newMovement.producto_id,
      lote_id: newMovement.lote_id,
      cantidad: newMovement.cantidad,
      deposito_origen_id: newMovement.deposito_origen_id,
      deposito_destino_id: newMovement.deposito_destino_id,
      usuario_ejecuta_id: newMovement.usuario_ejecuta_id,
      motivo: newMovement.motivo || undefined,
    }
    await api.post('/api/inventory/warehouses/product/move', payload)
    closeCreateModal()
    // refrescar lista
    fetchMovements()
  } catch (e) {
    // apiService ya muestra alert; mantenemos un mensaje local por si acaso
    createError.value = e?.response?.data?.message || 'Error al crear el movimiento'
  } finally {
    creating.value = false
  }
}

watch(() => newMovement.deposito_origen_id, async () => {
  if (syncingOriginFromLotSelection) {
    syncingOriginFromLotSelection = false
    return
  }
  createError.value = ''
  await fetchAvailableLots()
})

watch(() => newMovement.lote_selection, (selection) => {
  applyLotSelection(selection)
})

watch(() => newMovement.codigo_producto_input, (value) => {
  const code = String(value || '').trim()
  const resolvedCode = String(
    selectedProduct.value?.codigo_producto ||
    selectedProduct.value?.codigo_barras ||
    ''
  ).trim()
  if (selectedProduct.value && code && code !== resolvedCode) {
    selectedProduct.value = null
    availableLots.value = []
    productSuggestions.value = []
    newMovement.producto_id = null
    newMovement.lote_id = null
    newMovement.lote_selection = ''
  }
  if (!code) {
    selectedProduct.value = null
    availableLots.value = []
    productSuggestions.value = []
    newMovement.producto_id = null
    newMovement.lote_id = null
    newMovement.lote_selection = ''
  }
})

watch(productSearchQuery, (value) => {
  const query = String(value || '').trim()
  clearTimeout(productSearchDebounce)
  if (query.length < 2) {
    productSuggestions.value = []
    return
  }
  productSearchDebounce = setTimeout(() => {
    searchProductsForMovement(query)
  }, 250)
})
</script>

<style scoped>
.movements-page {
  max-width: 1400px;
}

.page-title-mobile {
  font-size: clamp(1.35rem, 2vw, 1.8rem);
}

.mobile-context-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 0.82rem;
}

.mobile-context-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
}

.operational-summary-card,
.mobile-filter-card {
  border-radius: 1rem;
}

.mobile-results-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: 1rem 0 1.15rem;
}

.mobile-results-pill {
  display: inline-flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 112px;
  padding: 0.8rem 0.95rem;
  border-radius: 1rem;
  background: #111827;
  color: #fff;
}

.mobile-results-pill.secondary {
  background: #f3f4f6;
  color: #111827;
}

.mobile-results-value {
  font-size: 1.1rem;
  font-weight: 700;
}

.mobile-results-value.small {
  font-size: 0.9rem;
}

.mobile-results-copy {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.8;
}

.mobile-movement-list {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.mobile-movement-card {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.mobile-movement-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
  margin-bottom: 0.85rem;
}

.mobile-movement-date {
  margin: 0 0 0.3rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b7280;
}

.mobile-movement-product {
  margin: 0;
  font-size: 1rem;
  line-height: 1.35;
  color: #0f172a;
}

.mobile-movement-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem;
}

.mobile-meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.mobile-meta-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.mobile-movement-note {
  margin-top: 0.9rem;
  color: #475569;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.presentation-trace {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  max-width: 34rem;
}

.presentation-trace-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.presentation-trace-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  color: #475569;
  font-size: 0.82rem;
  line-height: 1.35;
}

.mobile-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 2.5rem 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 1rem;
  background: #f8fafc;
  color: #64748b;
}

.mv-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1040;
}
.mv-backdrop-top {
  z-index: 1055;
}
.mv-modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  width: min(720px, 95vw);
  max-height: 90vh;
  overflow: auto;
  border-radius: .5rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  z-index: 1050;
}
.mv-modal-search {
  z-index: 1060;
}
.mv-header { padding: 1rem 1rem 0 1rem; }
.mv-body { padding: 1rem; }
.mv-footer { padding: 0 1rem 1rem 1rem; }

@media (max-width: 991.98px) {
  .page-header-mobile {
    align-items: stretch !important;
    flex-direction: column;
    gap: 0.85rem;
  }

  .btn-mobile-primary,
  .mobile-filter-card .btn-group,
  .mobile-filter-card .btn-group .btn {
    width: 100%;
  }

  .mobile-filter-card .btn-group {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    margin-right: 0 !important;
  }

  .mobile-filter-card .btn-group .btn {
    border-radius: 0.5rem !important;
  }

  .mv-modal {
    top: 0;
    left: 0;
    transform: none;
    width: 100vw;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
  }
}

@media (max-width: 575.98px) {
  .movements-page {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }

  .mobile-movement-top {
    flex-direction: column;
  }

  .mobile-movement-meta {
    grid-template-columns: 1fr;
  }
}
</style>
